<template>
  <div class="pl-coach-home">
    <!-- Attention banner: uncollected checkouts + bookings awaiting the coach.
         Only renders when there's something to act on. -->
    <div v-if="attentionCount > 0" class="bg-yellow-50 border border-solid border-yellow-400 rounded-sm p-sm m-b-md" data-cy="coach-home-attention">
      <div class="flex gap-x-sm align-items-start">
        <i class="fa-solid fa-triangle-exclamation text-yellow-600 m-t-xxs"></i>
        <div class="flex-1">
          <p class="m-b-none font-semi">{{ attentionCount }} {{ attentionCount === 1 ? 'item needs' : 'items need' }} your attention</p>
          <p class="m-b-none text-14 text-gray-600">{{ attentionSummary }}</p>
        </div>
      </div>
      <button type="button" class="btn btn-warning w-full m-t-sm" data-cy="attention-review" @click="showReview = !showReview">
        {{ showReview ? 'Hide' : 'Review' }}
      </button>
      <div v-if="showReview" class="m-t-sm">
        <div v-for="item in attentionItems" :key="item.coachBookingId" class="bg-white border border-solid border-gray-200 rounded-sm p-sm m-b-xs">
          <div class="flex justify-space-between align-items-center gap-x-sm">
            <div style="min-width: 0;">
              <p class="m-b-none font-semi">{{ item.startAt.format('ddd, MMM D') }} · {{ item.startAt.format('h:mm a') }}</p>
              <p class="m-b-none text-14 text-gray-600 pl-truncate">{{ item.wrestlerNames || 'No wrestlers listed' }}</p>
            </div>
            <span class="pl-slot-badge flex-shrink-0" :class="slotStatusBadgeClass(item.status)">{{ attentionLabel(item) }}</span>
          </div>
          <div class="flex gap-x-xs m-t-sm">
            <template v-if="item.kind === 'confirm'">
              <button type="button" class="btn btn-primary flex-1" data-cy="attention-confirm" @click="updateBookingStatus(item, 'confirmed')">
                <i class="fa-solid fa-check m-r-xxs"></i> Confirm booking
              </button>
              <button type="button" class="btn btn-danger btn-outline" @click="updateBookingStatus(item, 'declined')">
                Decline
              </button>
            </template>
            <a v-else :href="forAppEmbed ? undefined : lessonCheckoutHref(item.privateLessonId)" class="btn btn-primary w-full" data-cy="attention-checkout" @click="handleLessonCheckoutClick($event, item.privateLessonId)">
              Check out
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Equal first-class actions. Booking is the guardian flow: it stays for
         a coach with kids of their own (even one who isn't accepting — they
         book their kids with *other* coaches), but a coach without kids has
         nobody to book and would 403 on the book page. Publishing hours is
         only for coaches who can be booked. -->
    <div v-if="canBookLesson || accepting" class="grid gap-sm m-b-md" :class="canBookLesson && accepting ? 'grid-cols-2 sm-grid-cols-1' : 'grid-cols-1'">
      <a v-if="canBookLesson" :href="forAppEmbed ? undefined : bookLessonPath" class="btn btn-primary" data-cy="home-book-lesson" @click="handleNav($event, bookLessonPath, 'Select Coach')">
        <i class="fa-solid fa-plus m-r-xxs"></i> Book a Lesson
      </a>
      <a v-if="accepting" :href="forAppEmbed ? undefined : addAvailabilityPath" class="btn btn-default" data-cy="home-add-availability" @click="handleNav($event, addAvailabilityPath, 'Add Availability')">
        <i class="fa-solid fa-plus m-r-xxs"></i> Add Availability
      </a>
    </div>

    <!-- Accepting but unpriced: everything else on this page implies the
         coach is in business, yet families can't complete a booking until
         pricing exists — and only admins can assign it. Surface that here,
         not just on the profile page. -->
    <div v-if="accepting && !pricingSet" class="bg-yellow-50 border border-solid border-yellow-400 rounded-sm p-sm m-b-md" data-cy="coach-home-no-pricing">
      <div class="flex gap-x-sm align-items-start">
        <i class="fa-solid fa-triangle-exclamation text-yellow-600 m-t-xxs"></i>
        <div class="flex-1">
          <p class="m-b-none font-semi">Your pricing isn't set up yet</p>
          <p class="m-b-none text-14 text-gray-600">
            Families can't book you until pricing is assigned{{ isAdmin ? '.' : ' — ask your club admin to set your rates.' }}
          </p>
        </div>
      </div>
      <a v-if="isAdmin" :href="forAppEmbed ? undefined : profilePath" class="btn btn-warning w-full m-t-sm" data-cy="no-pricing-profile" @click="handleNav($event, profilePath, 'Profile & Rates')">
        Set up pricing
      </a>
    </div>

    <!-- Not accepting: the agenda would invite this coach to publish hours
         nobody can book — coach_events_controller returns an empty feed for
         them — so swap it for the one thing that fixes it. The attention
         banner above deliberately stays: lessons from before the toggle
         flipped still need checking out, and that's money. -->
    <div v-if="!accepting" class="bg-gray-50 border border-solid border-gray-200 rounded-sm p-md text-center m-b-md" data-cy="coach-home-not-accepting">
      <i class="fa-regular fa-calendar-xmark text-20 text-gray-400"></i>
      <p class="font-semi m-t-xs m-b-xxs">You're not accepting private lessons</p>
      <p class="text-14 text-gray-600 m-b-sm">
        Wrestlers and parents can't see your availability or book time with you.
        Turn it on to publish hours and start taking bookings.
      </p>
      <a :href="forAppEmbed ? undefined : profilePath" class="btn btn-primary" data-cy="not-accepting-profile" @click="handleNav($event, profilePath, 'Profile & Rates')">
        Set up private lessons
      </a>
    </div>

    <!-- Schedule: date strip + day-grouped slots with contextual actions -->
    <coach-availability-agenda
      v-if="accepting"
      ref="agenda"
      :coach="coachData"
      :new-availability-path="addAvailabilityPath"
      :compact="true"
      :show-lesson-actions="true"
      :for-app-embed="forAppEmbed"
      @booking-updated="fetchAttention"
    ></coach-availability-agenda>

    <!-- Growth action in its own section: it doesn't belong inside the
         "More" admin list (where an always-open panel broke the
         compact-row rhythm), but it also shouldn't outrank the agenda, so
         it sits just below it. The common case leads: most families are
         already on WrestlingIQ, and coaches should steer them to the app
         verbally rather than send them this URL — the sign-up link is only
         for families NOT on WrestlingIQ yet (it opens in the browser; the
         signup route is deliberately excluded from universal links).
         Showing the full URL lets a coach recognize and read it aloud, not
         just blind-copy it. Suppressed until pricing exists — sharing a
         link nobody can complete a booking on is premature; the no-pricing
         callout above owns that state. -->
    <template v-if="accepting && pricingSet && coachData.lessons_url">
      <h3 class="text-16 font-semi m-t-md m-b-xs">Your sign-up link</h3>
      <div data-cy="home-new-client-link">
        <p class="text-14 m-b-xs"><span class="font-semi">Already on WrestlingIQ?</span> <span class="text-gray-600">Most families are — just tell them to book you from the private lessons tab in the app. No link needed.</span></p>
        <p class="text-14 m-b-xs"><span class="font-semi">New family, not on WrestlingIQ yet?</span> <span class="text-gray-600">Text or email them this link — they create an account and book you in one step.</span></p>
        <div class="flex align-items-center gap-x-xs">
          <span class="flex-1 bg-gray-50 border border-solid border-gray-200 rounded-sm p-x-xs p-y-xxs text-14 text-gray-700 pl-truncate" style="min-width: 0;" data-cy="new-client-url">{{ lessonsUrlDisplay }}</span>
          <copy-icon class="btn btn-default btn-compact flex-shrink-0" :text="coachData.lessons_url" label="Copy link"></copy-icon>
        </div>
      </div>
    </template>

    <!-- Less-frequent destinations -->
    <h3 class="text-16 font-semi m-t-md m-b-xs">More</h3>
    <div class="border border-solid border-gray-200 rounded-sm">
      <div class="p-sm">
        <vertical-expander>
          <template v-slot:title>
            <p class="m-y-none font-semi">Earnings</p>
          </template>
          <template v-slot:content>
            <coach-lesson-earnings :coach-id="coachData.id"></coach-lesson-earnings>
          </template>
        </vertical-expander>
      </div>
      <a :href="forAppEmbed ? undefined : profilePath" class="flex justify-space-between align-items-center p-sm border-t border-solid border-gray-200 text-gray-900" data-cy="home-profile-link" @click="handleNav($event, profilePath, 'Profile & Rates')">
        <span>
          <span class="font-semi block">Your lesson profile &amp; rates</span>
          <span class="text-14 text-gray-600">Photo, bio, pricing, accepting bookings</span>
        </span>
        <i class="fa-solid fa-chevron-right text-gray-400"></i>
      </a>
      <div class="p-sm border-t border-solid border-gray-200">
        <vertical-expander>
          <template v-slot:title>
            <p class="m-y-none font-semi">Close open slots</p>
          </template>
          <template v-slot:content>
            <close-open-slots :coach="coachData" class="m-t-xs" @closed="onSlotsClosed"></close-open-slots>
          </template>
        </vertical-expander>
      </div>
    </div>

    <div v-show="error" class="m-t-sm">
      <small><span class="text-danger">{{ errorMessage }}</span></small>
    </div>
  </div>
</template>

<script>
import qs from 'qs';
import CoachAvailabilityAgenda from './coach_availability_agenda.vue';
import CoachLessonEarnings from './coach_lesson_earnings.vue';
import CloseOpenSlots from './close_open_slots.vue';
import CopyIcon from './copy_icon.vue';
import VerticalExpander from './vertical_expander.vue';
import { errorableMix } from './mixins/errorable_mix';
import { appEmbedMix } from './mixins/app_embed_mix';
import { privateLessonStatusMix } from './mixins/private_lesson_status_mix';
import { lessonCheckoutMix, privateLessonBookingMix } from './mixins/private_lesson_booking_mix';

// Attention lookback start. Deliberately unbounded in practice — an
// unchecked-out lesson is uncollected money no matter how old — so this just
// predates the feature rather than encoding a policy window.
const ATTENTION_LOOKBACK_START = '2020-01-01';

// The coach's "run my day" home at /private_lessons: act (attention banner) →
// do (book / add availability) → glance (agenda + week one-liner) →
// occasionally (earnings, profile & rates). Deliberately this-coach-only —
// admins reach the team command center from the sidebar, not from here.
// Storefront config lives on the profile & rates page; per-coach admin
// management stays on coaches/:id/dashboard.
export default {
  name: 'coach-lesson-home',
  components: {
    CoachAvailabilityAgenda,
    CoachLessonEarnings,
    CloseOpenSlots,
    CopyIcon,
    VerticalExpander,
  },
  mixins: [errorableMix, appEmbedMix, privateLessonStatusMix, lessonCheckoutMix, privateLessonBookingMix],
  props: {
    coach: {
      type: Object,
      required: true,
    },
    bookLessonPath: {
      type: String,
      required: true,
    },
    // Whether the coach has wrestlers of their own to book (guardian flow).
    canBookLesson: {
      type: Boolean,
      default: false,
    },
    addAvailabilityPath: {
      type: String,
      required: true,
    },
    profilePath: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      // Server-rendered coach JSON, replaced in place when the embed webview
      // is revealed again so accepting/pricing state isn't stale (same
      // pattern as private_lesson_coach_panel).
      coachData: _.cloneDeep(this.coach),
      attentionItems: [],
      showReview: false,
    };
  },
  computed: {
    // Admins land here whether or not they take lessons themselves, so this
    // drives an empty state rather than being assumed true.
    accepting() {
      return !!this.coachData.accepting_private_lessons;
    },
    // Same definition as coachSetupStatusMix#hasPricing — bookable requires
    // an assigned pricing tier, and only admins can assign one.
    pricingSet() {
      return (this.coachData.product_coach_listings || []).length > 0;
    },
    isAdmin() {
      return this.coachData.role === 'admin';
    },
    attentionCount() {
      return this.attentionItems.length;
    },
    // Human-readable version of the sign-up link — protocol stripped so the
    // visible text starts with the recognizable domain. The copy button still
    // copies the full URL.
    lessonsUrlDisplay() {
      return (this.coachData.lessons_url || '').replace(/^https?:\/\//, '');
    },
    attentionSummary() {
      const checkouts = this.attentionItems.filter((item) => item.kind === 'checkout').length;
      const confirms = this.attentionItems.filter((item) => item.kind === 'confirm').length;
      const parts = [];
      if (checkouts > 0) {
        parts.push(`${checkouts} ${checkouts === 1 ? 'lesson' : 'lessons'} to check out`);
      }
      if (confirms > 0) {
        parts.push(`${confirms} ${confirms === 1 ? 'booking' : 'bookings'} to confirm`);
      }
      return parts.join(' · ');
    },
  },
  mounted() {
    this.fetchAttention();
    // In the embed, setup edits happen on pushed native screens (profile &
    // rates, availability); when this webview is revealed again on pop,
    // refetch so the accepting/pricing empty states and agenda aren't stale.
    if (this.forAppEmbed) {
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }
  },
  beforeDestroy() {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  },
  methods: {
    onVisibilityChange() {
      if (document.visibilityState === 'visible') {
        this.refreshHome();
      }
    },
    refreshHome() {
      axios.get(this.$apiService.coachUrl(this.coachData.id))
        .then((response) => {
          this.coachData = response.data;
        });
      this.fetchAttention();
      // The agenda only exists while accepting; if the refetch just flipped
      // that on, it mounts fresh and fetches on its own.
      if (this.$refs.agenda) {
        this.$refs.agenda.refreshAll();
      }
    },
    // Two fetches: past events where the coach booking is still confirmed
    // (checkout never happened — it flips coach bookings to completed) or
    // invited, plus future invited bookings so a Thursday confirmation isn't
    // invisible on Tuesday. Lookback is deliberately unbounded: it's money.
    fetchAttention() {
      const vm = this;
      const today = moment();
      const past = vm.fetchEvents(ATTENTION_LOOKBACK_START, today.format('YYYY-MM-DD'), ['invited', 'confirmed']);
      const future = vm.fetchEvents(today.format('YYYY-MM-DD'), today.clone().add(13, 'months').format('YYYY-MM-DD'), ['invited']);
      Promise.all([past, future])
        .then(([pastEvents, futureEvents]) => {
          const seen = new Set();
          const items = [];
          pastEvents.concat(futureEvents).forEach((event) => {
            if (seen.has(event.id)) {
              return;
            }
            seen.add(event.id);
            const item = vm.attentionItemFor(event);
            if (item) {
              items.push(item);
            }
          });
          vm.attentionItems = items.sort((a, b) => a.startAt - b.startAt);
        })
        .catch((error) => {
          vm.errorMessage = vm.parseErrorResponse(error);
          vm.error = true;
        });
    },
    fetchEvents(start, end, statuses) {
      const params = {
        event_type: 'private_lesson',
        start,
        end,
        coach_ids: [this.coachData.id],
        event_booking_status_in: statuses,
        expand: ['event_bookings', 'private_lessons'],
      };
      const url = this.$apiService.eventsUrl(`?${qs.stringify(params, { arrayFormat: 'brackets' })}`);
      return this.$apiService.loadAllPages(url, 'events');
    },
    attentionItemFor(event) {
      const bookings = event.event_bookings || [];
      const coachBooking = bookings.find((b) => b.profile_type === 'CoachProfile' && b.profile_id === this.coachData.id);
      if (!coachBooking) {
        return null;
      }
      const wrestlerBookings = bookings.filter((b) => b.profile_type === 'WrestlerProfile');
      const endAt = moment(event.end_at);
      let kind = null;
      if (coachBooking.status === 'invited') {
        kind = 'confirm';
      } else if (coachBooking.status === 'confirmed'
        && endAt.isBefore(moment())
        && wrestlerBookings.some((b) => b.status === 'confirmed')) {
        kind = 'checkout';
      }
      if (!kind) {
        return null;
      }
      return {
        kind,
        eventId: event.id,
        coachBookingId: coachBooking.id,
        privateLessonId: (event.private_lessons && event.private_lessons[0]) ? event.private_lessons[0].id : null,
        startAt: moment(event.start_at),
        endAt,
        status: coachBooking.status,
        wrestlerNames: wrestlerBookings
          .filter((b) => ['invited', 'pending', 'confirmed', 'completed'].includes(b.status))
          .map((b) => (b.profile ? b.profile.display_name : 'Unknown'))
          .join(', '),
      };
    },
    attentionLabel(item) {
      return item.kind === 'checkout' ? 'Needs checkout' : this.slotStatusLabel(item.status);
    },
    updateBookingStatus(item, status) {
      const vm = this;
      vm.updateCoachBookingStatus({
        coachBookingId: item.coachBookingId,
        status,
        actorProfileId: vm.coachData.id,
        onSuccess() {
          vm.fetchAttention();
          if (vm.$refs.agenda) {
            vm.$refs.agenda.refreshAll();
          }
        },
      });
    },
    handleNav(e, path, title) {
      if (this.forAppEmbed) {
        e.preventDefault();
        this.askAppToPush(`${window.location.origin}${path}`, title);
      }
    },
    onSlotsClosed() {
      if (this.$refs.agenda) {
        this.$refs.agenda.refreshAll();
      }
    },
  },
};
</script>
